// Shared validation utilities
import { z } from 'zod';

// Common validation schemas
export const emailSchema = z.string().email('Invalid email address');

export const phoneSchema = z.string().min(10, 'Phone number must be at least 10 digits');

export const passwordSchema = z.string()
  .min(8, 'Password must be at least 8 characters')
  .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
  .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
  .regex(/[0-9]/, 'Password must contain at least one number')
  .regex(/[!@#$%^&*(),.?":{}|<>]/, 'Password must contain at least one special character');

export const nameSchema = z.string()
  .min(2, 'Name must be at least 2 characters')
  .max(50, 'Name must be less than 50 characters')
  .regex(/^[a-zA-Z\s]+$/, 'Name can only contain letters and spaces');

// Order validation schemas
export const orderSchema = z.object({
  customerId: z.string().min(1, 'Customer ID is required'),
  garmentType: z.string().min(1, 'Garment type is required'),
  measurements: z.record(z.number().positive('Measurement must be positive')),
  dueDate: z.date().min(new Date(), 'Due date must be in the future'),
  priority: z.enum(['low', 'medium', 'high']),
  specialNotes: z.string().optional(),
  price: z.number().positive('Price must be positive')
});

export const measurementSchema = z.object({
  name: z.string().min(1, 'Measurement name is required'),
  value: z.number().positive('Measurement value must be positive'),
  unit: z.string().min(1, 'Unit is required'),
  type: z.enum(['body', 'garment', 'custom'])
});

// User validation schemas
export const userRegistrationSchema = z.object({
  email: emailSchema,
  password: passwordSchema,
  fullName: nameSchema,
  phone: phoneSchema,
  role: z.enum(['customer', 'tailor'])
});

// Simplified registration schema for backend (only required fields)
export const backendRegistrationSchema = z.object({
  email: emailSchema,
  password: passwordSchema,
  name: nameSchema,
  role: z.enum(['customer', 'tailor'])
});

export const userProfileSchema = z.object({
  fullName: nameSchema,
  email: emailSchema,
  phone: phoneSchema,
  address: z.object({
    street: z.string().min(1, 'Street address is required'),
    city: z.string().min(1, 'City is required'),
    state: z.string().min(1, 'State is required'),
    zipCode: z.string().min(5, 'ZIP code must be at least 5 characters'),
    country: z.string().min(1, 'Country is required')
  }).optional()
});

// Business validation schemas
export const businessProfileSchema = z.object({
  businessName: z.string().min(1, 'Business name is required'),
  businessType: z.enum(['tailor', 'fashion_house', 'boutique']),
  description: z.string().max(500, 'Description must be less than 500 characters'),
  serviceAreas: z.array(z.string()).min(1, 'At least one service area is required'),
  specialties: z.array(z.string()).optional(),
  pricing: z.object({
    consultationFee: z.number().min(0),
    hourlyRate: z.number().min(0),
    minimumOrder: z.number().min(0)
  }).optional()
});

// Validation helper functions
export const validateEmail = (email: string) => {
  try {
    emailSchema.parse(email);
    return { isValid: true, error: null };
  } catch (_error) {
    if (error instanceof z.ZodError && error.errors && error.errors.length > 0) {
      return { isValid: false, error: error.errors[0].message };
    }
    return { isValid: false, error: 'Invalid email' };
  }
};

export const validatePhone = (phone: string) => {
  try {
    phoneSchema.parse(phone);
    return { isValid: true, error: null };
  } catch (_error) {
    if (error instanceof z.ZodError && error.errors && error.errors.length > 0) {
      return { isValid: false, error: error.errors[0].message };
    }
    return { isValid: false, error: 'Invalid phone number' };
  }
};

export const validatePassword = (password: string) => {
  try {
    passwordSchema.parse(password);
    return { isValid: true, error: null };
  } catch (_error) {
    return { isValid: false, error: error instanceof z.ZodError ? error.errors[0].message : 'Invalid password' };
  }
};

export const validateForm = <T>(data: unknown, schema: z.ZodSchema<T>) => {
  console.log('🔍 validateForm called with:', { data, schema });
  
  try {
    const result = schema.parse(data);
    console.log('🔍 Validation successful:', result);
    return { success: true, data: result, errors: {} };
  } catch (_error) {
    console.log('🔍 Validation error:', error);
    console.log('🔍 Error type:', typeof error);
    console.log('🔍 Error instanceof ZodError:', error instanceof z.ZodError);
    
    if (error instanceof z.ZodError) {
      console.log('🔍 ZodError.issues:', error.issues);
      const errors = error.issues.reduce((acc, err) => {
        const path = err.path.join('.');
        acc[path] = err.message;
        return acc;
      }, {} as Record<string, string>);
      console.log('🔍 Processed errors:', errors);
      return { success: false, data: null, errors };
    }
    return { success: false, data: null, errors: { general: 'Validation failed' } };
  }
};

// Form field validation helpers
export const getFieldError = (errors: Record<string, string> | null, fieldName: string) => {
  if (!errors) return null;
  return errors[fieldName] || null;
};

export const hasFieldError = (errors: Record<string, string> | null, fieldName: string) => {
  return Boolean(getFieldError(errors, fieldName));
};
